import {
  DEAL_RATING,
  ESTIMATED_PRICE_DIFFERENCE,
  ESTIMATED_SAVINGS,
  MOST_RECENT,
  PRICE,
} from './translations';
import { DomainKey } from './types/product';

// ─── Domain Registry ────────────────────────────────────────────────
// Single source of truth for all domain configuration.
// To add a new domain: add one entry here + categories below + translations.

export type SearchConfig = 'english' | 'simple';

export interface DomainRegistryEntry {
  name: string;
  displayName: string;
  tld: string;
  currency: string;
  affiliateTag: string;
  adRegion: string | null;
  adScriptUrl: string | null;
  searchConfig: SearchConfig;
  warehouseSellerId: string | null;
}

export const DOMAIN_REGISTRY: Record<DomainKey, DomainRegistryEntry> = {
  '1': {
    name: 'US',
    displayName: 'United States',
    tld: 'com',
    currency: 'USD',
    affiliateTag: 'dealforager-20',
    adRegion: 'US',
    adScriptUrl: '//z-na.amazon-adsystem.com/widgets/onejs?MarketPlace=US',
    searchConfig: 'english',
    warehouseSellerId: 'A2L77EE7U53NWQ',
  },
  '2': {
    name: 'UK',
    displayName: 'United Kingdom',
    tld: 'co.uk',
    currency: 'GBP',
    affiliateTag: 'dealforager-21',
    adRegion: 'GB',
    adScriptUrl:
      '//z-eu.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&MarketPlace=GB',
    searchConfig: 'english',
    warehouseSellerId: 'A2OAJ7377F756P',
  },
  '3': {
    name: 'DE',
    displayName: 'Deutschland',
    tld: 'de',
    currency: 'EUR',
    affiliateTag: 'dealforager0b-21',
    adRegion: 'DE',
    adScriptUrl:
      '//z-eu.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&MarketPlace=DE',
    searchConfig: 'simple',
    warehouseSellerId: 'A8KICS1PHF7ZO',
  },
  '4': {
    name: 'FR',
    displayName: 'France',
    tld: 'fr',
    currency: 'EUR',
    affiliateTag: 'dealforager0d-21',
    adRegion: 'FR',
    adScriptUrl:
      '//z-eu.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&MarketPlace=FR',
    searchConfig: 'simple',
    warehouseSellerId: 'A2CVHYRTWLQO9T',
  },
  '5': {
    name: 'JP',
    displayName: '\u65E5\u672C',
    tld: 'co.jp',
    currency: 'JPY',
    affiliateTag: '',
    adRegion: null,
    adScriptUrl: null,
    searchConfig: 'simple',
    warehouseSellerId: 'ANNLLU4HTZVX9',
  },
  '6': {
    name: 'CA',
    displayName: 'Canada',
    tld: 'ca',
    currency: 'CAD',
    affiliateTag: 'dealforager02-20',
    adRegion: 'CA',
    adScriptUrl:
      '//z-fe.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&Marketplace=CA',
    searchConfig: 'english',
    warehouseSellerId: 'AUJBRIGYRJ3Q8',
  },
  '8': {
    name: 'IT',
    displayName: 'Italia',
    tld: 'it',
    currency: 'EUR',
    affiliateTag: 'dealforage0c1-21',
    adRegion: 'IT',
    adScriptUrl:
      '//z-fe.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&Marketplace=IT',
    searchConfig: 'simple',
    warehouseSellerId: 'A1HO9729ND375Y',
  },
  '9': {
    name: 'ES',
    displayName: 'Espa\u00F1a',
    tld: 'es',
    currency: 'EUR',
    affiliateTag: 'dealforager0a-21',
    adRegion: 'ES',
    adScriptUrl:
      '//z-fe.amazon-adsystem.com/widgets/q?ServiceVersion=20070822&Operation=GetScript&ID=OneJS&WS=1&Marketplace=ES',
    searchConfig: 'simple',
    warehouseSellerId: 'A6T89FGPU3U0Q',
  },
  '10': {
    name: 'IN',
    displayName: 'India',
    tld: 'in',
    currency: 'INR',
    affiliateTag: '',
    adRegion: null,
    adScriptUrl: null,
    searchConfig: 'english',
    warehouseSellerId: null,
  },
  '11': {
    name: 'MX',
    displayName: 'M\u00E9xico',
    tld: 'com.mx',
    currency: 'MXN',
    affiliateTag: '',
    adRegion: null,
    adScriptUrl: null,
    searchConfig: 'simple',
    warehouseSellerId: 'A23KQ2J0V65IPS',
  },
  '12': {
    name: 'BR',
    displayName: 'Brasil',
    tld: 'com.br',
    currency: 'BRL',
    affiliateTag: '',
    adRegion: null,
    adScriptUrl: null,
    searchConfig: 'simple',
    warehouseSellerId: 'A3CO2Z4EZ33K8R',
  },
};

// ─── Derived exports (preserve existing import signatures) ──────────

function deriveMap<T>(fn: (entry: DomainRegistryEntry) => T): Record<DomainKey, T> {
  const result = {} as Record<DomainKey, T>;
  for (const [k, v] of Object.entries(DOMAIN_REGISTRY)) {
    result[k as DomainKey] = fn(v);
  }
  return result;
}

export const DOMAINS: Record<DomainKey, string> = deriveMap((e) => e.name);

// VISIBLE_DOMAINS: only domains with Amazon Warehouse (warehouseSellerId set), using native display names
export const VISIBLE_DOMAINS: Partial<Record<DomainKey, string>> = (() => {
  const m: Partial<Record<DomainKey, string>> = {};
  for (const [k, v] of Object.entries(DOMAIN_REGISTRY)) {
    if (v.warehouseSellerId) m[k as DomainKey] = v.displayName;
  }
  return m;
})();

// Display order for the domain dropdown (most important first)
export const DOMAIN_DISPLAY_ORDER: DomainKey[] = ['1', '6', '2', '3', '9', '4', '5', '8', '11', '12'];

export const DOMAIN_MAP: Record<DomainKey, string> = deriveMap((e) => e.tld);
export const CURRENCY_MAP: Record<DomainKey, string> = deriveMap((e) => e.currency);
export const DOMAIN_AFFILIATE_MAP: Record<DomainKey, string> = deriveMap((e) => e.affiliateTag);

// AD_DOMAINS: only domains with adRegion set
export const AD_DOMAINS: Partial<Record<DomainKey, string>> = (() => {
  const m: Partial<Record<DomainKey, string>> = {};
  for (const [k, v] of Object.entries(DOMAIN_REGISTRY)) {
    if (v.adRegion) m[k as DomainKey] = v.adRegion;
  }
  return m;
})();

// DOMAIN_ADS_SCRIPT_MAP: only domains with adScriptUrl set
export const DOMAIN_ADS_SCRIPT_MAP: Partial<Record<DomainKey, string>> = (() => {
  const m: Partial<Record<DomainKey, string>> = {};
  for (const [k, v] of Object.entries(DOMAIN_REGISTRY)) {
    if (v.adScriptUrl) m[k as DomainKey] = v.adScriptUrl;
  }
  return m;
})();

// DOMAIN_WAREHOUSE_SELLER_MAP: only domains with warehouseSellerId set
export const DOMAIN_WAREHOUSE_SELLER_MAP: Partial<Record<DomainKey, string>> = (() => {
  const m: Partial<Record<DomainKey, string>> = {};
  for (const [k, v] of Object.entries(DOMAIN_REGISTRY)) {
    if (v.warehouseSellerId) m[k as DomainKey] = v.warehouseSellerId;
  }
  return m;
})();

// Per-domain rootcats that should use merchant-filtered /dp/ links (Vinyl, Movies & TV)
export const VINYL_ROOTCATS: Partial<Record<DomainKey, number[]>> = {
  '1': [5174, 2625373011],
  '2': [229816],
  '3': [255882],
  '6': [916514],
};

export const DOMAIN_KEYS: DomainKey[] = Object.keys(DOMAIN_REGISTRY) as DomainKey[];

// ─── Domain Auto-Detection ──────────────────────────────────────────
// Priority: URL param > localStorage > timezone detection > US fallback

const STORAGE_KEY = 'dealforager_domain';

// IANA timezone → domain. Longest prefix match wins.
// Canada/Mexico/Brazil share America/* with US, so specific cities go first.
const TIMEZONE_TO_DOMAIN: [string, DomainKey][] = [
  // Canada
  ['America/Toronto', '6'],
  ['America/Vancouver', '6'],
  ['America/Edmonton', '6'],
  ['America/Winnipeg', '6'],
  ['America/Halifax', '6'],
  ['America/St_Johns', '6'],
  ['America/Regina', '6'],
  ['America/Whitehorse', '6'],
  ['America/Yellowknife', '6'],
  ['America/Iqaluit', '6'],
  ['America/Moncton', '6'],
  // Mexico
  ['America/Mexico_City', '11'],
  ['America/Cancun', '11'],
  ['America/Monterrey', '11'],
  ['America/Merida', '11'],
  ['America/Chihuahua', '11'],
  ['America/Hermosillo', '11'],
  ['America/Mazatlan', '11'],
  ['America/Tijuana', '11'],
  ['America/Matamoros', '11'],
  ['America/Ojinaga', '11'],
  ['America/Bahia_Banderas', '11'],
  // Brazil
  ['America/Sao_Paulo', '12'],
  ['America/Fortaleza', '12'],
  ['America/Recife', '12'],
  ['America/Bahia', '12'],
  ['America/Belem', '12'],
  ['America/Manaus', '12'],
  ['America/Cuiaba', '12'],
  ['America/Porto_Velho', '12'],
  ['America/Campo_Grande', '12'],
  ['America/Araguaina', '12'],
  ['America/Maceio', '12'],
  ['America/Noronha', '12'],
  ['America/Rio_Branco', '12'],
  ['America/Boa_Vista', '12'],
  ['America/Santarem', '12'],
  ['America/Eirunepe', '12'],
  // US — everything else in America/ that didn't match above
  ['America/', '1'],
  // Europe
  ['Europe/London', '2'],
  ['Europe/Berlin', '3'],
  ['Europe/Paris', '4'],
  ['Europe/Rome', '8'],
  ['Europe/Madrid', '9'],
  // Japan
  ['Asia/Tokyo', '5'],
  // India
  ['Asia/Kolkata', '10'],
  ['Asia/Calcutta', '10'],
];

function detectDomainFromTimezone(): DomainKey | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return null;
    for (const [prefix, domain] of TIMEZONE_TO_DOMAIN) {
      if (tz.startsWith(prefix)) return domain;
    }
  } catch { /* SSR or old browser */ }
  return null;
}

/** Get the best default domain: saved preference > timezone > US. */
export function getDefaultDomain(): DomainKey {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && DOMAIN_KEYS.indexOf(saved as any) >= 0) {
      return saved as DomainKey;
    }
  } catch { /* localStorage unavailable */ }

  const detected = detectDomainFromTimezone();
  if (detected && VISIBLE_DOMAINS[detected]) {
    return detected;
  }
  return '1';
}

/** Persist a domain choice to localStorage. */
export function saveDomainPreference(domain: DomainKey): void {
  try {
    localStorage.setItem(STORAGE_KEY, domain);
  } catch { /* localStorage unavailable */ }
}

// ─── Categories ─────────────────────────────────────────────────────

function objectFlip(obj: { [key in DomainKey]: { [key: string]: number } }) {
  const ret = {} as { [key in DomainKey]: { [key: number]: string } };
  for (const domain of DOMAIN_KEYS) {
    ret[domain] = {};
    for (const key in obj[domain]) {
      ret[domain][obj[domain][key]] = key;
    }
  }
  return ret;
}

export type US_CATEGORIES =
  | 2619525011
  | 2617941011
  | 15684181
  | 165796011
  | 3760911
  | 2335752011
  | 7141123011
  | 172282
  | 2972638011
  | 3760901
  | 16310101
  | 1055398
  | 16310091
  | 11091801
  | 2625373011
  | 1064954
  | 2619533011
  | 3375251
  | 228013
  | 165793011
  | 468642
  | 5174
  | 1;

export const CATEGORIES: { [key in DomainKey]: { [key: string]: number } } = {
  '1': {
    Appliances: 2619525011,
    'Arts, Crafts & Sewing': 2617941011,
    'Auto Parts & Accessories': 15684181,
    Baby: 165796011,
    'Beauty & Personal Care': 3760911,
    'Cell Phones & Accessories': 2335752011,
    'Clothing, Shoes, & Jewelry': 7141123011,
    Electronics: 172282,
    'Garden & Outdoor': 2972638011,
    'Grocery & Gourmet Food': 16310101,
    'Health & Household': 3760901,
    'Home & Kitchen': 1055398,
    'Industrial & Scientific': 16310091,
    'Musical Instruments': 11091801,
    'Movies & TV': 2625373011,
    'Office Products': 1064954,
    'Pet Supplies': 2619533011,
    'Sports & Outdoors': 3375251,
    'Tools & Home Improvement': 228013,
    'Toys & Games': 165793011,
    'Video Games': 468642,
    Vinyl: 5174,
    Other: 1,
  },
  '2': {
    Baby: 59624031,
    Beauty: 117332031,
    'Car & Motorbike': 248877031,
    Clothing: 83450031,
    'Computers & Accessories': 340831031,
    'DIY & Tools': 79903031,
    'Electronics & Photo': 560798,
    Fashion: 11961407031,
    'Garden & Outdoors': 11052671,
    Grocery: 340834031,
    'Health & Personal Care': 65801031,
    'Home & Kitchen': 11052681,
    'Industrial & Scientific': 5866054031,
    Jewellery: 193716031,
    'Large Appliances': 908798031,
    Lighting: 213077031,
    Luggage: 2454166031,
    'Musical Instruments': 340837031,
    'PC and Video Games': 300703,
    'Pet Supplies': 340840031,
    'Shoes & Bags': 355005011,
    'Sports & Outdoors': 318949011,
    'Stationery & Office': 192413031,
    'Toys & Games': 468292,
    Watches: 328228011,
    Vinyl: 229816,
    Other: 1,
  },
  '3': {
    'Auto & Motorrad': 78191031,
    Baby: 355007011,
    Baumarkt: 80084031,
    Bekleidung: 77028031,
    Beleuchtung: 213083031,
    'Bürobedarf & Schreibwaren': 192416031,
    'Computer & Zubehör': 340843031,
    'Elektro-Großgeräte': 908823031,
    'Elektronik & Foto': 562066,
    Garten: 10925031,
    'Gesundheit & Körperpflege': 64187031,
    Haustier: 340852031,
    'Industrie & Wissenschaft': 5866098031,
    'Koffer & Taschen': 2454118031,
    'Küche, Haushalt & Wohnen': 3167641,
    Musikinstrumente: 340849031,
    'Parfümerie & Kosmetik': 84230031,
    Schmuck: 327472011,
    'Schuhe & Handtaschen': 355006011,
    Spielzeug: 12950651,
    'Sport & Freizeit': 16435051,
    Uhren: 193707031,
    Videospiele: 300992,
    Fashion: 11961464031,
    Vinyl: 255882,
    Andere: 1,
  },
  '4': {
    Animalerie: 1571268031,
    'Auto et Moto': 1571265031,
    Bagages: 2454145031,
    'Beauté et Parfum': 197858031,
    'Bébé et Puériculture': 206617031,
    Bricolage: 590748031,
    Bijoux: 193710031,
    'Chaussures et Sacs': 215934031,
    'Cuisine et Maison': 57004031,
    Électronique: 13921051,
    'Fournitures de Bureau': 192419031,
    'Gros Électroménager': 908826031,
    'Hygiène et Santé': 197861031,
    'Industriel et Scientifique': 5866109031,
    Informatique: 340858031,
    'Instruments de Musique': 340861031,
    Jardin: 3557027031,
    'Jeux Vidéo': 530490,
    'Jeux et Jouets': 322086011,
    'Luminaires et Eclairage': 213080031,
    Montres: 60649031,
    'Sports et Loisirs': 325614031,
    'Vêtements et Accessoires': 340855031,
    Mode: 11961521031,
    Autre: 1,
  },
  '5': {
    '\u30AC\u30FC\u30C7\u30F3\u30FB\u5DE5\u5177': 2016929051,
    '\u30AD\u30C3\u30BA\u30FB\u73A9\u5177': 13299531,
    '\u30AD\u30C3\u30C1\u30F3\u7528\u54C1\u30FB\u98DF\u5668': 3828871,
    '\u30B2\u30FC\u30E0\u30FB\u30DB\u30D3': 637394,
    '\u30B3\u30B9\u30E1\u30FB\u5065\u5EB7': 160384011,
    '\u30B8\u30E5\u30A8\u30EA\u30FC': 85895051,
    '\u30B9\u30DD\u30FC\u30C4': 14304371,
    '\u30D1\u30BD\u30B3\u30F3\u30FB\u5468\u8FBA\u6A5F\u5668': 2127209051,
    '\u30D5\u30A1\u30C3\u30B7\u30E7\u30F3': 352484011,
    '\u30D9\u30D3\u30FC': 344845011,
    '\u30DA\u30C3\u30C8': 2127212051,
    '\u6587\u623F\u5177': 86731051,
    '\u697D\u5668': 2123629051,
    '\u7523\u696D\u30FB\u79D1\u5B66': 3445393051,
    '\u8155\u6642\u8A08': 324025011,
    '\u8ECA\u30FB\u30D0\u30A4\u30AF': 2017304051,
    '\u96FB\u5B50\u6A5F\u5668\u30FB\u30AB\u30E1\u30E9': 3210981,
    '\u9774\u30FB\u30D0\u30C3\u30B0': 2016926051,
    '\u5BB6\u96FB': 2277724051,
    '\u96D1\u8CA8': 1,
  },
  '6': {
    Automotive: 6948389011,
    Baby: 3561346011,
    Beauty: 6205124011,
    'Clothing & Accessories': 21204935011,
    Electronics: 667823011,
    'Health & Personal Care': 6205177011,
    'Home & Kitchen': 2206275011,
    'Industrial & Scientific ': 11076213011,
    Jewelry: 6205496011,
    'Luggage & Bags': 6205505011,
    'Musical Instruments': 6916844011,
    'Office Products': 6205511011,
    'Patio, Lawn & Garden': 6205499011,
    'Pet Supplies': 6205514011,
    'Shoes & Handbags': 8604916011,
    'Sports & Outdoors': 2242989011,
    'Tools & Home Improvement': 3006902011,
    'Toys & Games': 6205517011,
    'Video Games': 3198031,
    Watches: 2235620011,
    Vinyl: 916514,
    Other: 1,
  },
  '8': {
    Abbigliamento: 2844433031,
    'Auto e Moto': 1571280031,
    Bellezza: 6198082031,
    'Casa e Cucina': 524015031,
    Elettronica: 412609031,
    'Fai da te': 2454160031,
    'Giardino e giardinaggio': 635016031,
    'Giochi e Giocattoli': 523997031,
    Gioielli: 2454163031,
    'Grandi elettrodomestici': 14437356031,
    Illuminazione: 1571292031,
    'Industria e Scienza': 5866068031,
    Informatica: 425916031,
    Orologi: 524009031,
    'Prima Infanzia': 1571286031,
    'Prodotti per Animali': 12472499031,
    'Prodotti per Ufficio': 3606310031,
    'Salute e cura della persona': 1571289031,
    'Scarpe e Borse': 524006031,
    'Sport e Tempo Libero': 524012031,
    'Strumenti Musicali': 3628629031,
    Valigeria: 2454148031,
    Videogiochi: 412603031,
    Moda: 5512286031,
    Altra: 1,
  },
  '9': {
    'Accesorios de Autom\u00F3viles': 1951051031,
    'Beb\u00E9': 1703495031,
    Belleza: 6198054031,
    'Bricolaje y Herramientas': 2454133031,
    'Deportes y Aire Libre': 2454136031,
    'Electrodom\u00E9sticos Grandes': 4772050031,
    'Electr\u00F3nica': 599370031,
    Equipaje: 2454129031,
    'Hogar y Cocina': 599391031,
    'Iluminaci\u00F3n': 3564289031,
    Industriales: 5866088031,
    'Inform\u00E1tica': 667049031,
    'Instrumentos Musicales': 3628866031,
    'Jard\u00EDn': 1571259031,
    'Joyer\u00EDa': 2454126031,
    'Juguetes y Juegos': 599385031,
    'Oficina y Papeler\u00EDa': 3628728031,
    'Productos para Mascotas': 12472654031,
    Relojes: 599388031,
    'Ropa y Accesorios': 2846220031,
    'Salud y Cuidado Personal': 3677430031,
    Videojuegos: 599382031,
    'Zapatos y Complementos': 1571262031,
    Moda: 5512276031,
    Otros: 1,
  },
  '10': {
    'Appliances': 5122348031,
    'Baby': 1571274031,
    'Bags, Wallets & Luggage': 2454169031,
    'Beauty': 1355016031,
    'Car & Motorbike': 4772060031,
    'Clothing & Accessories': 1571271031,
    'Computers & Accessories': 976392031,
    'Electronics': 976419031,
    'Grocery & Gourmet Foods': 2454178031,
    'Health & Personal Care': 1350384031,
    'Home & Kitchen': 976442031,
    'Home Improvement': 3704992031,
    'Industrial & Scientific': 5866078031,
    'Jewellery': 1951048031,
    'Musical Instruments': 3677697031,
    'Office Products': 2454172031,
    'Outdoor Living': 2454175031,
    'Pet Supplies': 2454181031,
    'Shoes & Handbags': 1571283031,
    'Sports, Fitness & Outdoors': 1984443031,
    'Toys & Games': 1350380031,
    'Watches': 1350387031,
    Other: 1,
  },
  '11': {
    'Alimentos y Bebidas': 17608484011,
    'Automotriz y Motocicletas': 13848848011,
    'Beb\u00E9': 9482650011,
    'Belleza': 11260452011,
    'Deportes y Aire Libre': 9482660011,
    'Electr\u00F3nicos': 9482558011,
    'Herramientas y Mejoras del Hogar': 9482670011,
    'Hogar y Cocina': 9482593011,
    'Industria, Empresas y Ciencia': 11076223011,
    'Instrumentos Musicales': 13848858011,
    'Jard\u00EDn': 16215357011,
    'Juguetes y Juegos': 11260442011,
    'Oficina y Papeler\u00EDa': 9673844011,
    'Productos para Animales': 11782336011,
    'Ropa, Zapatos y Accesorios': 13848838011,
    'Salud y Cuidado Personal': 9482610011,
    'Videojuegos': 9482640011,
    Otros: 1,
  },
  '12': {
    'Automotivo': 18914209011,
    'Beb\u00EAs': 17242603011,
    'Beleza': 16194414011,
    'Brinquedos e Jogos': 16194299011,
    'Casa': 16191000011,
    'Computadores e Inform\u00E1tica': 16339926011,
    'Cozinha': 16957125011,
    'Eletrodom\u00E9sticos': 16522082011,
    'Eletr\u00F4nicos e Tecnologia': 16209062011,
    'Esporte, Aventura e Lazer': 17349396011,
    'Ferramentas e Materiais de Constru\u00E7\u00E3o': 16957182011,
    'Games e Consoles': 7791985011,
    'Ind\u00FAstria e Com\u00E9rcio': 18991195011,
    'Instrumentos Musicais': 18991252011,
    'Jardim e Piscina': 18991021011,
    'Moda': 17365811011,
    'Papelaria e Escrit\u00F3rio': 16957239011,
    'Pet Shop': 18991136011,
    'Sa\u00FAde e Bem-Estar': 16215417011,
    Outros: 1,
  },
};

export const CATEGORIES_REVERSE: {
  [key in DomainKey]: { [key: string]: string };
} = objectFlip(CATEGORIES);

export const OTHER_CATEGORIES: { [key in DomainKey]: number[] } = {
  '1': [4991425011, 16333372011, 209470, 9007199254740991, 9223372036854775807],
  '2': [5512286031, 5512276031, 72911031, 12598575031, 9007199254740991, 3146281],
  '3': [5512286031, 5512276031, 11961407031, 64274031, 72921031, 12598632031, 9007199254740991],
  '4': [72919031, 530488, 5512286031, 5512276031, 11961407031, 11961464031, 3635788031, 9007199254740991],
  '5': [579684, 637392, 9007199254740991, 57239051, 4976279051, 52374051],
  '6': [916516, 9007199254740991],
  '8': [5512276031, 425919031, 11961407031, 11961464031, 6198092031, 9007199254740991],
  '9': [5512286031, 11961407031, 11961464031, 11961521031, 667040031, 9007199254740991],
  '10': [9007199254740991],
  '11': [9007199254740991],
  '12': [9007199254740991],
};

export const DEFAULT_CATEGORIES: { [key in DomainKey]: number[] } = {
  '1': [
    172282, 228013, 1055398, 1064954, 3375251, 3760901, 3760911, 11091801,
    16310091, 16310101, 165796011, 2335752011, 2617941011, 2619525011,
    2619533011, 2972638011, 7141123011, 4991425011, 468642,
  ],
  '2': [
    59624031, 117332031, 248877031, 83450031, 340831031, 79903031, 560798,
    11961407031, 11052671, 340834031, 65801031, 11052681, 5866054031,
    193716031, 908798031, 213077031, 2454166031, 340837031, 340840031,
    355005011, 318949011, 192413031, 468292, 328228011, 300703,
    ...OTHER_CATEGORIES['2'],
  ],
  '3': [
    355006011, 12950651, 213083031, 908823031, 16435051, 64187031, 327472011,
    192416031, 78191031, 340852031, 355007011, 3167641, 2454118031, 10925031,
    77028031, 84230031, 5866098031, 340843031, 80084031, 340849031, 193707031,
    300992, 11961464031,
    ...OTHER_CATEGORIES['3'],
  ],
  '4': [
    1571268031, 1571265031, 2454145031, 197858031, 590748031, 206617031,
    215934031, 57004031, 192419031, 197861031, 5866109031, 340861031,
    3557027031, 322086011, 213080031, 325614031, 340855031, 13921051,
    193710031, 60649031, 908826031, 530490, 11961521031,
    ...OTHER_CATEGORIES['4'],
  ],
  '5': [
    2016929051,
    13299531,
    3828871,
    637394,
    160384011,
    85895051,
    14304371,
    2127209051,
    352484011,
    344845011,
    2127212051,
    86731051,
    2123629051,
    3445393051,
    324025011,
    2017304051,
    3210981,
    2016926051,
    2277724051,
    ...OTHER_CATEGORIES['5'],
  ],
  '6': [
    6948389011,
    3561346011,
    6205124011,
    21204935011,
    667823011,
    6205177011,
    2206275011,
    11076213011,
    6205496011,
    6205505011,
    6916844011,
    6205511011,
    6205499011,
    6205514011,
    8604916011,
    2242989011,
    3006902011,
    6205517011,
    2235620011,
    3198031,
    ...OTHER_CATEGORIES['6'],
  ],
  '8': [
    2844433031, 1571280031, 6198082031, 524015031, 412609031, 2454160031,
    635016031, 523997031, 2454163031, 14437356031, 1571292031, 5866068031,
    524009031, 1571286031, 12472499031, 3606310031, 1571289031, 524006031,
    524012031, 3628629031, 2454148031, 412603031, 5512286031,
    ...OTHER_CATEGORIES['8'],
  ],
  '9': [
    3677430031, 4772050031, 599388031, 2846220031, 1571262031, 667049031,
    3628866031, 599385031, 6198054031, 12472654031, 599391031, 1703495031,
    2454133031, 5866088031, 2454129031, 3628728031, 1951051031, 3564289031,
    1571259031, 2454136031, 599370031, 2454126031, 599382031, 5512276031,
    ...OTHER_CATEGORIES['9'],
  ],
  '10': [
    5122348031, 1571274031, 2454169031, 1355016031, 4772060031, 1571271031,
    976392031, 976419031, 2454178031, 1350384031, 976442031, 3704992031,
    5866078031, 1951048031, 3677697031, 2454172031, 2454175031, 2454181031,
    1571283031, 1984443031, 1350380031, 1350387031,
    ...OTHER_CATEGORIES['10'],
  ],
  '11': [
    17608484011, 13848848011, 9482650011, 11260452011, 9482660011,
    9482558011, 9482670011, 9482593011, 11076223011, 13848858011,
    16215357011, 11260442011, 9673844011, 11782336011, 13848838011,
    9482610011, 9482640011,
    ...OTHER_CATEGORIES['11'],
  ],
  '12': [
    18914209011, 17242603011, 16194414011, 16194299011, 16191000011,
    16339926011, 16957125011, 16522082011, 16209062011, 17349396011,
    16957182011, 7791985011, 18991195011, 18991252011, 18991021011,
    17365811011, 16957239011, 18991136011, 16215417011,
    ...OTHER_CATEGORIES['12'],
  ],
};

export const CATEGORY_KEYS: { [key in DomainKey]: string[] } = (() => {
  const result = {} as { [key in DomainKey]: string[] };
  for (const k of DOMAIN_KEYS) {
    result[k] = Object.keys(CATEGORIES[k]);
  }
  return result;
})();

export const CATEGORY_VALUES: { [key in DomainKey]: number[] } = (() => {
  const result = {} as { [key in DomainKey]: number[] };
  for (const k of DOMAIN_KEYS) {
    result[k] = Object.values(CATEGORIES[k]);
  }
  return result;
})();

export const SORT_ORDERS = [
  {
    id: '0',
    name: DEAL_RATING,
  },
  {
    id: '1',
    name: ESTIMATED_SAVINGS,
  },
  {
    id: '2',
    name: ESTIMATED_PRICE_DIFFERENCE,
  },
  {
    id: '3',
    name: PRICE,
  },
];
export const MOST_RECENT_SORT_ORDER = { id: '4', name: MOST_RECENT };
export const SORT_ORDER_KEYS = ['0', '1', '2', '3'];

export const PRODUCT_PAGE_SIZE = 91;

export const AFFILIATE_ENABLED = true;
